import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Megaphone, Users, ArrowLeft, Trash2, Edit2, Vote } from 'lucide-react';
import { hasFullAccess } from '../lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAMPAIGN_TYPES = [
  'WDC President', 'Mayor', 'WDC', 'Local Council', 'Parliamentary', 'Presidential Election'
];

export default function CampaignsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const constituency_id = searchParams.get('constituency_id');
  const [campaigns, setCampaigns] = useState([]);
  const [candidates, setCandidates] = useState({});
  const [allCandidates, setAllCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [showCandidateDialog, setShowCandidateDialog] = useState(false);
  const [showEditCampaignDialog, setShowEditCampaignDialog] = useState(false);
  const [showEditCandidateDialog, setShowEditCandidateDialog] = useState(false);
  const [showDeleteCampaignDialog, setShowDeleteCampaignDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignToDelete, setCampaignToDelete] = useState('');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [newCampaign, setNewCampaign] = useState({ name: '', campaign_type: '', campaign_date: '', voting_start_time: '', voting_end_time: '' });
  const [newCandidate, setNewCandidate] = useState({ name: '', role: '', isExisting: false, existingCandidateId: '' });

  useEffect(() => {
    if (constituency_id) {
      loadCampaigns();
      loadAllCandidates();
    }
    // loadCampaigns/loadAllCandidates depend on constituency_id; stable identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constituency_id]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/campaigns?constituency_id=${constituency_id}`, {
        withCredentials: true
      });
      setCampaigns(response.data);
      
      for (const campaign of response.data) {
        loadCandidates(campaign.campaign_id);
      }
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadAllCandidates = async () => {
    try {
      const response = await axios.get(`${API}/candidates?constituency_id=${constituency_id}`, {
        withCredentials: true
      });
      setAllCandidates(response.data);
    } catch (error) {
      console.error('Failed to load all candidates');
    }
  };

  const loadCandidates = async (campaign_id) => {
    try {
      const response = await axios.get(`${API}/candidates?campaign_id=${campaign_id}`, {
        withCredentials: true
      });
      setCandidates(prev => ({ ...prev, [campaign_id]: response.data }));
    } catch (error) {
      console.error('Failed to load candidates:', error);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.campaign_type) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(
        `${API}/campaigns`,
        { ...newCampaign, constituency_id },
        { withCredentials: true }
      );
      toast.success('Campaign created');
      setShowCampaignDialog(false);
      setNewCampaign({ name: '', campaign_type: '', campaign_date: '', voting_start_time: '', voting_end_time: '' });
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleEditCampaign = async () => {
    if (!editingCampaign.name || !editingCampaign.campaign_type) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.put(
        `${API}/campaigns/${editingCampaign.campaign_id}`,
        {
          name: editingCampaign.name,
          campaign_type: editingCampaign.campaign_type,
          campaign_date: editingCampaign.campaign_date,
          voting_start_time: editingCampaign.voting_start_time,
          voting_end_time: editingCampaign.voting_end_time
        },
        { withCredentials: true }
      );
      toast.success('Campaign updated');
      setShowEditCampaignDialog(false);
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update campaign');
    }
  };

  const openEditCampaign = (campaign) => {
    setEditingCampaign({
      campaign_id: campaign.campaign_id,
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      campaign_date: campaign.campaign_date || '',
      voting_start_time: campaign.voting_start_time || '',
      voting_end_time: campaign.voting_end_time || ''
    });
    setShowEditCampaignDialog(true);
  };

  const handleCreateCandidate = async () => {
    if (newCandidate.isExisting) {
      if (!newCandidate.existingCandidateId) {
        toast.error('Please select a candidate');
        return;
      }
      const existingCandidate = allCandidates.find(c => `${c.name}_${c.role}` === newCandidate.existingCandidateId);
      if (!existingCandidate) {
        toast.error('Candidate not found');
        return;
      }
      
      try {
        await axios.post(
          `${API}/candidates`,
          { name: existingCandidate.name, role: existingCandidate.role, campaign_id: selectedCampaign },
          { withCredentials: true }
        );
        toast.success('Candidate added');
        setShowCandidateDialog(false);
        setNewCandidate({ name: '', role: '', isExisting: false, existingCandidateId: '' });
        loadCandidates(selectedCampaign);
        loadAllCandidates();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add candidate');
      }
    } else {
      if (!newCandidate.name || !newCandidate.role) {
        toast.error('Please fill all fields');
        return;
      }

      try {
        await axios.post(
          `${API}/candidates`,
          { ...newCandidate, campaign_id: selectedCampaign },
          { withCredentials: true }
        );
        toast.success('Candidate added');
        setShowCandidateDialog(false);
        setNewCandidate({ name: '', role: '', isExisting: false, existingCandidateId: '' });
        loadCandidates(selectedCampaign);
        loadAllCandidates();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add candidate');
      }
    }
  };

  const [deleteConfirmStep, setDeleteConfirmStep] = useState(false);

  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) {
      toast.error('Please select a campaign to delete');
      return;
    }

    if (!deleteConfirmStep) {
      setDeleteConfirmStep(true);
      return;
    }

    try {
      await axios.delete(`${API}/campaigns/${campaignToDelete}`, { withCredentials: true });
      toast.success('Campaign deleted');
      setShowDeleteCampaignDialog(false);
      setCampaignToDelete('');
      setDeleteConfirmStep(false);
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
      setDeleteConfirmStep(false);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteCampaignDialog(false);
    setCampaignToDelete('');
    setDeleteConfirmStep(false);
  };

  const handleEditCandidate = async () => {
    if (!editingCandidate.name || !editingCandidate.role) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.put(
        `${API}/candidates/${editingCandidate.candidate_id}`,
        {
          name: editingCandidate.name,
          role: editingCandidate.role
        },
        { withCredentials: true }
      );
      toast.success('Candidate updated');
      setShowEditCandidateDialog(false);
      setEditingCandidate(null);
      loadCampaigns();
      loadAllCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update candidate');
    }
  };

  const openEditCandidate = (candidate) => {
    setEditingCandidate({
      candidate_id: candidate.candidate_id,
      name: candidate.name,
      role: candidate.role
    });
    setShowEditCandidateDialog(true);
  };

  const handleDeleteCandidate = async (candidateId, campaignId) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) {
      return;
    }

    try {
      await axios.delete(`${API}/candidates/${candidateId}`, { withCredentials: true });
      toast.success('Candidate deleted');
      loadCandidates(campaignId);
      loadAllCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete candidate');
    }
  };

  const canEdit = hasFullAccess(user) || user?.effective_permissions?.includes('campaigns.edit');

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <Button variant="ghost" onClick={() => navigate('/constituencies')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Constituencies
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Campaigns
            </h1>
            <p className="text-muted-foreground">Manage campaigns and candidates</p>
          </div>
          {canEdit && (
            <div className="flex gap-2 mt-4 md:mt-0">
              <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
                <DialogTrigger asChild>
                  <Button className="mt-4 md:mt-0" data-testid="add-campaign-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Campaign
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Campaign</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Campaign Name</Label>
                      <Input
                        value={newCampaign.name}
                        onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                        placeholder="e.g., LCE 26"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select
                        value={newCampaign.campaign_type}
                        onValueChange={(v) => setNewCampaign({ ...newCampaign, campaign_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CAMPAIGN_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Voting Day (Optional)</Label>
                      <Input
                        type="date"
                        value={newCampaign.campaign_date}
                        onChange={(e) => setNewCampaign({ ...newCampaign, campaign_date: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Voting Opens (Optional)</Label>
                        <Input
                          type="time"
                          value={newCampaign.voting_start_time}
                          onChange={(e) => setNewCampaign({ ...newCampaign, voting_start_time: e.target.value })}
                          placeholder="07:00"
                        />
                      </div>
                      <div>
                        <Label>Voting Closes (Optional)</Label>
                        <Input
                          type="time"
                          value={newCampaign.voting_end_time}
                          onChange={(e) => setNewCampaign({ ...newCampaign, voting_end_time: e.target.value })}
                          placeholder="18:00"
                        />
                      </div>
                    </div>
                    <Button onClick={handleCreateCampaign} className="w-full">Create</Button>
                  </div>
                </DialogContent>
              </Dialog>

              {campaigns.length > 0 && (
                <Dialog open={showDeleteCampaignDialog} onOpenChange={(open) => {
                  if (!open) closeDeleteDialog();
                  else setShowDeleteCampaignDialog(true);
                }}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" className="mt-4 md:mt-0" data-testid="remove-campaign-btn">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Campaign
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Remove Campaign</DialogTitle>
                    </DialogHeader>
                    {!deleteConfirmStep ? (
                      <div className="space-y-4">
                        <div>
                          <Label>Select Campaign to Remove</Label>
                          <Select value={campaignToDelete} onValueChange={setCampaignToDelete}>
                            <SelectTrigger data-testid="campaign-delete-select">
                              <SelectValue placeholder="Choose campaign" />
                            </SelectTrigger>
                            <SelectContent>
                              {campaigns.map(campaign => (
                                <SelectItem key={campaign.campaign_id} value={campaign.campaign_id} data-testid={`campaign-option-${campaign.campaign_id}`}>
                                  {campaign.name} ({campaign.campaign_type})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleDeleteCampaign} variant="destructive" className="w-full" data-testid="confirm-delete-campaign-btn">
                          Delete Selected Campaign
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-800 font-medium">Are you sure?</p>
                          <p className="text-red-600 text-sm mt-1">
                            This will permanently delete the campaign and all associated candidates and records.
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirmStep(false)}>
                            Cancel
                          </Button>
                          <Button variant="destructive" className="flex-1" onClick={handleDeleteCampaign} data-testid="final-delete-btn">
                            Yes, Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No campaigns yet</h3>
              <p className="text-muted-foreground mb-4">Create your first campaign</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {campaigns.map(campaign => (
              <Card key={campaign.campaign_id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-2xl">{campaign.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {campaign.campaign_type}
                        {campaign.campaign_date && ` • Voting Day: ${new Date(campaign.campaign_date).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/records?constituency_id=${constituency_id}&campaign_id=${campaign.campaign_id}`);
                        }}
                      >
                        View Records
                      </Button>
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/fiyavalhu?constituency_id=${constituency_id}&campaign_id=${campaign.campaign_id}`);
                        }}
                      >
                        <Vote className="h-4 w-4 mr-2" />
                        Fiyavalhu
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditCampaign(campaign);
                            }}
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCampaign(campaign.campaign_id);
                              setShowCandidateDialog(true);
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Candidate
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Candidates
                  </h3>
                  {candidates[campaign.campaign_id]?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {candidates[campaign.campaign_id].map(candidate => (
                        <div
                          key={candidate.candidate_id}
                          className="p-3 border rounded-lg bg-secondary/50 relative group"
                        >
                          <p className="font-medium pr-16">{candidate.name}</p>
                          <p className="text-sm text-muted-foreground">{candidate.role}</p>
                          {canEdit && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0"
                                onClick={() => openEditCandidate(candidate)}
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-destructive"
                                onClick={() => handleDeleteCandidate(candidate.candidate_id, campaign.campaign_id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No candidates added yet</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showCandidateDialog} onOpenChange={setShowCandidateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Candidate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <Button
                  type="button"
                  variant={newCandidate.isExisting ? 'outline' : 'default'}
                  onClick={() => setNewCandidate({ name: '', role: '', isExisting: false, existingCandidateId: '' })}
                  className="flex-1"
                >
                  Create New
                </Button>
                <Button
                  type="button"
                  variant={newCandidate.isExisting ? 'default' : 'outline'}
                  onClick={() => setNewCandidate({ name: '', role: '', isExisting: true, existingCandidateId: '' })}
                  className="flex-1"
                >
                  Select Existing
                </Button>
              </div>

              {newCandidate.isExisting ? (
                <div>
                  <Label>Select Candidate</Label>
                  <Select
                    value={newCandidate.existingCandidateId}
                    onValueChange={(v) => setNewCandidate({ ...newCandidate, existingCandidateId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose candidate" />
                    </SelectTrigger>
                    <SelectContent>
                      {allCandidates.map((candidate) => (
                        <SelectItem key={`${candidate.name}_${candidate.role}`} value={`${candidate.name}_${candidate.role}`}>
                          {candidate.name} - {candidate.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div>
                    <Label>Candidate Name</Label>
                    <Input
                      value={newCandidate.name}
                      onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })}
                      placeholder="e.g., Fathimath Rashfa"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Input
                      value={newCandidate.role}
                      onChange={(e) => setNewCandidate({ ...newCandidate, role: e.target.value })}
                      placeholder="e.g., Councilor, Mayor, WDC"
                    />
                  </div>
                </>
              )}
              <Button onClick={handleCreateCandidate} className="w-full">Add Candidate</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Campaign Dialog */}
        <Dialog open={showEditCampaignDialog} onOpenChange={setShowEditCampaignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Campaign</DialogTitle>
            </DialogHeader>
            {editingCampaign && (
              <div className="space-y-4">
                <div>
                  <Label>Campaign Name</Label>
                  <Input
                    value={editingCampaign.name}
                    onChange={(e) => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={editingCampaign.campaign_type}
                    onValueChange={(v) => setEditingCampaign({ ...editingCampaign, campaign_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CAMPAIGN_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Voting Day (Optional)</Label>
                  <Input
                    type="date"
                    value={editingCampaign.campaign_date}
                    onChange={(e) => setEditingCampaign({ ...editingCampaign, campaign_date: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Voting Opens</Label>
                    <Input
                      type="time"
                      value={editingCampaign.voting_start_time}
                      onChange={(e) => setEditingCampaign({ ...editingCampaign, voting_start_time: e.target.value })}
                      placeholder="07:00"
                    />
                  </div>
                  <div>
                    <Label>Voting Closes</Label>
                    <Input
                      type="time"
                      value={editingCampaign.voting_end_time}
                      onChange={(e) => setEditingCampaign({ ...editingCampaign, voting_end_time: e.target.value })}
                      placeholder="18:00"
                    />
                  </div>
                </div>
                <Button onClick={handleEditCampaign} className="w-full">Update Campaign</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Candidate Dialog */}
        <Dialog open={showEditCandidateDialog} onOpenChange={setShowEditCandidateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Candidate</DialogTitle>
            </DialogHeader>
            {editingCandidate && (
              <div className="space-y-4">
                <div>
                  <Label>Candidate Name</Label>
                  <Input
                    value={editingCandidate.name}
                    onChange={(e) => setEditingCandidate({ ...editingCandidate, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input
                    value={editingCandidate.role}
                    onChange={(e) => setEditingCandidate({ ...editingCandidate, role: e.target.value })}
                  />
                </div>
                <Button onClick={handleEditCandidate} className="w-full">Update Candidate</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}